import { useState } from 'react'
import { Card } from '../../types'
import backMylUrl from '../../assets/cards/back_myl.png'

const R2_BASE_URL = 'https://pub-f45ccbb772a5401ebe950820bd15eecf.r2.dev'

function transformLocalImagePath(localImage: string): string {
  let path = localImage
  
  path = path.replace(/^images\//, '')
  
  path = path.replace(/\.png$/i, '.webp')
  path = path.replace(/\.jpg$/i, '.webp')
  path = path.replace(/\.jpeg$/i, '.webp')
  
  path = path.replace(/ /g, '_')
  
  path = path.normalize('NFD').replace(/[\u0300-\u036f]/g, '')
  
  return path
}

function getCardImageUrl(card: Card): string | undefined {
  const cardAny = card as any
  
  if (card.imagePath) {
    let imagePath = card.imagePath
    if (!imagePath.includes('.webp')) {
      imagePath = transformLocalImagePath(imagePath)
    }
    return `${R2_BASE_URL}/${imagePath}`
  }
  
  if (card.imageUrl) {
    return card.imageUrl
  }
  
  const localImage = cardAny.localImage || cardAny.local_image
  if (localImage) {
    const transformedPath = transformLocalImagePath(localImage)
    return `${R2_BASE_URL}/${transformedPath}`
  }
  
  return undefined
}

interface CardImageProps {
  card: Card
  className?: string
  showBackAsMain?: boolean
}

export function CardImage({ card, className = '', showBackAsMain = false }: CardImageProps) {
  const [hasError, setHasError] = useState(false)

  const imageUrl = getCardImageUrl(card)

  if (!imageUrl || hasError || showBackAsMain) {
    return <CardPlaceholder card={card} className={className} showBackAsMain={showBackAsMain} />
  }

  return (
    <img
      src={imageUrl}
      alt={card.name}
      className={className}
      onError={() => setHasError(true)}
    />
  )
}

export function CardImageModal({ card }: { card: Card }) {
  const [hasError, setHasError] = useState(false)
  const [isFlipped, setIsFlipped] = useState(false)

  const imageUrl = getCardImageUrl(card)

  if (!imageUrl || hasError) {
    return (
      <div className="w-full max-w-sm mx-auto">
        <img
          src={backMylUrl}
          alt="Back"
          className="w-full h-auto object-contain rounded-2xl shadow-2xl"
        />
      </div>
    )
  }

  return (
    <div
      className="relative w-full max-w-md mx-auto cursor-pointer"
      onClick={(e) => {
        e.stopPropagation()
        setIsFlipped(!isFlipped)
      }}
    >
      <div
        className={`relative transition-all duration-500 ${isFlipped ? 'rotate-y-180' : ''}`}
        style={{
          transformStyle: 'preserve-3d',
          transform: isFlipped ? 'rotateY(180deg)' : 'rotateY(0deg)'
        }}
      >
        <div className="rounded-2xl shadow-2xl overflow-hidden backface-hidden">
          <img
            src={imageUrl}
            alt={card.name}
            className="w-full h-auto object-contain"
            onError={() => setHasError(true)}
          />
        </div>
        <div
          className="absolute inset-0 rounded-2xl shadow-2xl overflow-hidden backface-hidden"
          style={{ transform: 'rotateY(180deg)' }}
        >
          <img
            src={backMylUrl}
            alt="Back"
            className="w-full h-auto object-contain"
          />
        </div>
      </div>
    </div>
  )
}

interface CardPlaceholderProps {
  card: Card
  className?: string
  isModal?: boolean
  showBackAsMain?: boolean
}

function CardPlaceholder({ card, className = '', isModal = false, showBackAsMain = false }: CardPlaceholderProps) {
  const cardAny = card as any
  const hasAnyData = card.goldCost !== undefined ||
                     cardAny.race ||
                     cardAny.habilidad ||
                     cardAny.Habilidad ||
                     cardAny.skill ||
                     cardAny.ability ||
                     (card.effect && Object.keys(card.effect).length > 0)

  if (showBackAsMain) {
    return (
      <div className={`relative flex items-center justify-center bg-[#1e232a] overflow-hidden ${className}`}>
        <img
          src={backMylUrl}
          alt="Back"
          className="w-full h-full object-contain"
        />
      </div>
    )
  }

  if (!hasAnyData) {
    return (
      <div className={`relative flex items-center justify-center bg-[#1e232a] overflow-hidden ${className}`}>
        <img
          src={backMylUrl}
          alt="Back"
          className="absolute inset-0 w-full h-full object-cover opacity-20"
        />
        <span className="relative z-10 text-white/40 font-medium">Sin info</span>
      </div>
    )
  }

  const effect = card.effect as any
  const ability = effect?.description || 
                  effect?.skill || 
                  cardAny.habilidad || 
                  cardAny.Habilidad || 
                  cardAny.skill || 
                  cardAny.ability || 
                  ''
  const epicText = cardAny.epicText || ''
  const race = cardAny.race || cardAny.Raza || ''

  if (isModal) {
    return (
      <div 
        className={`relative rounded-2xl shadow-2xl flex flex-col items-center justify-center overflow-hidden border border-[#2d3540] bg-[#1e232a] ${className}`}
      >
        <img 
          src={backMylUrl} 
          alt="Back" 
          className="absolute inset-0 w-full h-full object-cover opacity-25"
        />
        <div className="absolute inset-0 bg-gradient-to-br from-slate-900/50 via-transparent to-slate-900/50" />
        
        <div className="relative z-10">
          <div className="absolute top-5 left-5">
            {card.strength !== undefined && (
              <div className="w-14 h-14 bg-red-500 rounded-full flex items-center justify-center text-2xl text-white font-bold shadow-lg border-2 border-red-400">
                {card.strength}
              </div>
            )}
          </div>
          
          <div className="absolute top-5 right-5">
            <div className="w-14 h-14 bg-amber-500 rounded-full flex items-center justify-center text-2xl text-[#0f1115] font-bold shadow-lg border-2 border-amber-400">
              {card.cost}
            </div>
          </div>
          
          <div className="flex flex-col items-center px-4 mt-6">
            <span className="text-3xl font-bold text-white/80 text-center leading-tight px-2">{card.name}</span>
          </div>
          
          {race && (
            <div className="absolute top-24 right-5">
              <span className="px-3 py-1.5 bg-slate-600/80 rounded-lg text-sm text-slate-300 font-medium border border-slate-500/50">
                {race}
              </span>
            </div>
          )}

          {ability && (
            <div className="absolute inset-x-5 bottom-32 top-auto z-10">
              <div className="p-4 bg-[#0f1115]/90 rounded-xl border border-[#2d3540]/50 max-h-40 overflow-y-auto">
                <p className="text-sm text-[#d1d5db] leading-relaxed">{ability}</p>
              </div>
            </div>
          )}

          {epicText && (
            <div className="absolute inset-x-5 bottom-5 top-auto z-10">
              <div className="p-3 bg-purple-900/40 rounded-xl border border-purple-500/30">
                <p className="text-xs text-purple-300 leading-relaxed italic">{epicText}</p>
              </div>
            </div>
          )}

          <div className="absolute bottom-5 left-1/2 -translate-x-1/2 flex items-center gap-3">
            {card.goldCost !== undefined && (
              <div className="flex items-center gap-1.5 px-3 py-1.5 bg-yellow-500/20 border border-yellow-500/30 rounded-xl">
                <span className="text-sm text-yellow-400 font-medium">Oro:</span>
                <span className="text-sm text-yellow-300 font-bold">{card.goldCost}</span>
              </div>
            )}
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className={`relative flex flex-col h-full bg-[#1e232a] ${className}`}>
      <img 
        src={backMylUrl} 
        alt="Back" 
        className="absolute inset-0 w-full h-full object-cover opacity-20"
      />
      <div className="absolute inset-0 bg-gradient-to-b from-[#1e232a]/80 via-transparent to-[#1e232a]/80" />
      
      <div className="relative z-10 w-full h-full flex flex-col">
        <div className="flex-1 flex flex-col items-center justify-center mt-2 overflow-hidden pt-2">
          <span className="text-xl font-bold text-white/80 text-center leading-tight px-2">{card.name}</span>
          
          {ability && (
            <div className="mt-2 px-2 w-full">
              <p className="text-[9px] text-[#8b919a] text-center line-clamp-3">{ability}</p>
            </div>
          )}
          
          {epicText && (
            <div className="mt-1 px-2 w-full">
              <p className="text-[8px] text-purple-400 text-center line-clamp-2 italic">{epicText}</p>
            </div>
          )}
        </div>
        
        {race && (
          <span className="text-[10px] text-slate-400 text-center py-1">{race}</span>
        )}
        
        <div className="text-center pb-2">
          {card.goldCost !== undefined && (
            <span className="text-[10px] text-yellow-400">Oro: {card.goldCost}</span>
          )}
        </div>
      </div>

      <div className="absolute top-2 left-2 z-20">
        {card.strength !== undefined && (
          <div className="w-8 h-8 bg-red-500 rounded-full flex items-center justify-center text-sm text-white font-bold shadow border border-red-400">
            {card.strength}
          </div>
        )}
      </div>
      
      <div className="absolute top-2 right-2 z-20">
        <div className="w-8 h-8 bg-amber-500 rounded-full flex items-center justify-center text-sm text-[#0f1115] font-bold shadow border-amber-400">
          {card.cost}
        </div>
      </div>
    </div>
  )
}
